import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useApi } from '../../lib/api';
import usePolling from '../../lib/usePolling';
import Pagination from '../../components/shared/Pagination';

const PAGE_SIZE = 10;

const STATUS_LABELS = {
  open: 'Upload your invoice',
  disputed: 'Question raised',
  confirmed: 'Submitted, admin reviewing',
  pending_payment: 'Approved, payment scheduled',
  paid: 'Paid',
  failed: 'Payment failed',
};

const STATUS_PILL = {
  open: 's-amber',
  disputed: 's-violet',
  confirmed: 's-green',
  pending_payment: 's-blue',
  paid: 's-grey',
  failed: 's-red',
};

const ROW_RAIL_RUNNING = '';
const ROW_RAIL = {
  open: 'r-amber',
  disputed: 'r-violet',
  confirmed: 'r-green',
  pending_payment: 'r-blue',
  paid: '',
  failed: 'r-red',
};

// Invoices are submitted only in the 25th–5th billing window (window_open
// comes from the API). A failed payment is retried by admin, so it sits with
// the other in-review states.
const TABS = [
  { key: 'all', label: 'All cycles' },
  { key: 'action', label: 'Needs your action', match: (c) => c.status === 'open' && c.window_open },
  { key: 'review', label: 'In review', match: (c) => ['confirmed', 'disputed', 'pending_payment', 'failed'].includes(c.status) },
  { key: 'running', label: 'Upcoming', match: (c) => c.status === 'open' && !c.window_open },
  { key: 'paid', label: 'Paid', match: (c) => c.status === 'paid' },
];

function rupees(paise) {
  return `₹${((paise || 0) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function monthEndLabel(periodMonth) {
  const d = new Date(periodMonth);
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return lastDay.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

// 'YYYY-MM-DD' (IST calendar date from the API) -> "25 Oct 2026".
function fmtDay(ymd) {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function monthHasEnded(periodMonth) {
  const d = new Date(periodMonth);
  return new Date() >= new Date(d.getFullYear(), d.getMonth() + 1, 1);
}

function MessageThread({ messages }) {
  return (
    <div className="msg-scroll" style={{ maxHeight: 300, overflowY: 'auto', marginBottom: 12 }}>
      {messages.map((m) => (
        <div key={m.id} style={{ marginBottom: 12 }}>
          <b style={{ fontSize: 13.5 }}>{m.author_role === 'admin' ? 'GLOS Admin' : 'You'}</b>
          <div className="sub">{new Date(m.created_at).toLocaleString()}</div>
          <p style={{ fontSize: 13.5, marginTop: 2, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{m.body}</p>
        </div>
      ))}
    </div>
  );
}

const INVOICE_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.webp';
const INVOICE_MAX_SIZE_BYTES = 50 * 1024 * 1024;

export default function PartnerBilling() {
  const api = useApi();
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('all');
  const [page, setPage] = useState(1);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const res = await api.partnerListCycles();
      setCycles(res.items || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { refresh(); }, [refresh]);
  usePolling(refresh, 30000, !selected);

  if (selected) {
    return <CycleDetail cycleId={selected} onBack={() => { setSelected(null); refresh(); }} />;
  }

  const activeTab = TABS.find((t) => t.key === tab) || TABS[0];
  const filtered = activeTab.match ? cycles.filter(activeTab.match) : cycles;
  const pagedCycles = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <header className="bar">
        <div>
          <h1>Billing</h1>
          <p>One cycle per month. Upload an invoice, get it approved, get paid.</p>
        </div>
      </header>

      <section className="view">
        <div className="tabs">
          {TABS.map((t) => (
            <button key={t.key} className={`tab${tab === t.key ? ' on' : ''}`} onClick={() => { setTab(t.key); setPage(1); }}>
              {t.label}
            </button>
          ))}
        </div>

        {error && <div className="admin-banner error">{error}</div>}

        {loading ? (
          <p className="muted">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="panel empty">
            <b>No billing cycles here</b>
            <p>{cycles.length === 0 ? 'Cycles appear once your clients start making payments.' : 'Nothing in this tab right now.'}</p>
          </div>
        ) : (
          <>
            <div className="panel tbl-scroll">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Cycle</th>
                    <th>Commission</th>
                    <th>Client payments</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedCycles.map((c) => (
                    <tr key={c.id} className={`bill-row ${c.status === 'open' && !c.window_open ? ROW_RAIL_RUNNING : (ROW_RAIL[c.status] || '')}`}>
                      <td>
                        <b style={{ fontWeight: 600 }}>{new Date(c.period_month).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</b>
                        <div className="sub">{monthHasEnded(c.period_month) ? 'Closed' : 'Closes'} {monthEndLabel(c.period_month)}</div>
                      </td>
                      <td><span className="money">{rupees(c.computed_amount_paise)}</span></td>
                      <td>{c.entry_count}</td>
                      <td>
                        {c.status === 'open' && !c.window_open
                          ? <span className="state s-grey" title={`Submission window: ${fmtDay(c.window_opens_on)} – ${fmtDay(c.window_closes_on)}`}><i></i>Opens {fmtDay(c.window_opens_on)}</span>
                          : <span className={`state ${STATUS_PILL[c.status] || 's-grey'}`}><i></i>{STATUS_LABELS[c.status] || c.status}</span>}
                      </td>
                      <td>
                        <button className="act" onClick={() => setSelected(c.id)}>
                          {c.status === 'open' && c.window_open ? 'Upload invoice' : 'View'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalItems={filtered.length} pageSize={PAGE_SIZE} onPageChange={setPage} />
          </>
        )}
      </section>
    </>
  );
}

// Index 0 = "Cycle closed" (always reached — a cycle only becomes
// actionable once its month has ended), 1 = "Your invoice", 2 = "Admin
// approval", 3 = "Payment".
const STEP_STATES = {
  open:            ['done', 'now',  '',     ''    ],
  confirmed:       ['done', 'done', 'now',  ''    ],
  disputed:        ['done', 'done', 'now',  ''    ],
  pending_payment: ['done', 'done', 'done', 'now' ],
  failed:          ['done', 'done', 'done', 'now' ],
  paid:            ['done', 'done', 'done', 'done'],
};

function stepsFor(status) {
  return STEP_STATES[status] || STEP_STATES.open;
}

function CycleDetail({ cycleId, onBack }) {
  const api = useApi();
  const { partner, refreshPartner } = useOutletContext() || {};
  const suspended = partner?.status === 'suspended';
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [reportedAmount, setReportedAmount] = useState('');
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [uploaded, setUploaded] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [disputeBody, setDisputeBody] = useState('');
  const [messageBody, setMessageBody] = useState('');

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const res = await api.partnerGetCycle(cycleId);
      setData(res);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }, [api, cycleId]);

  useEffect(() => { refresh(); }, [refresh]);
  // Pick up admin replies without a manual refresh.
  usePolling(refresh, 15000);

  if (error) return <div className="admin-banner error" style={{ margin: '20px 36px' }}>{error}</div>;
  if (!data) return <p className="muted" style={{ margin: '20px 36px' }}>Loading…</p>;

  const { cycle, ledger, messages } = data;
  const monthLabel = new Date(cycle.period_month).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

  const windowOpen = !!cycle.window_open;
  const reportedPaise = reportedAmount ? Math.round(Number(reportedAmount) * 100) : null;
  const amountMismatch = cycle.computed_amount_paise > 0 && reportedPaise != null && reportedPaise !== cycle.computed_amount_paise;

  // Shared by the file picker and drag-and-drop. `accept` on the input does
  // not apply to dropped files, so the extension is checked here too.
  const selectFile = (f) => {
    setFile(null);
    setUploaded(null);
    setFileError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!f) return;
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    if (!INVOICE_ACCEPT.split(',').includes(`.${ext}`)) {
      setFileError('Unsupported file type. Use PDF, Word, Excel, CSV or an image.');
      return;
    }
    if (f.size > INVOICE_MAX_SIZE_BYTES) {
      setFileError('Max file size is 50 MB');
      return;
    }
    setFile(f);
  };

  const handleFileChange = (e) => selectFile(e.target.files?.[0] || null);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (busy) return;
    selectFile(e.dataTransfer.files?.[0] || null);
  };

  const clearFile = (e) => {
    e?.stopPropagation();
    selectFile(null);
  };

  const handleUpload = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const presign = await api.partnerPresignInvoice(cycleId, {
        file_name: file.name, content_type: file.type, file_size: file.size,
      });
      const putRes = await fetch(presign.upload_url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      if (!putRes.ok) throw new Error(`Upload failed (${putRes.status}). Please try again.`);
      setUploaded(presign);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.partnerConfirmCycle(cycleId, {
        file_url: uploaded?.file_url,
        file_key: uploaded?.file_key,
        file_name: file?.name,
        invoice_number: invoiceNumber.trim() || undefined,
        partner_reported_amount_paise: reportedAmount ? Math.round(Number(reportedAmount) * 100) : undefined,
      });
      refresh();
      refreshPartner?.();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleDispute = async () => {
    if (!disputeBody.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.partnerDisputeCycle(cycleId, { body: disputeBody.trim() });
      setDisputeBody('');
      refresh();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleMessage = async () => {
    if (!messageBody.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.partnerPostCycleMessage(cycleId, { body: messageBody.trim() });
      setMessageBody('');
      refresh();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <header className="bar">
        <div>
          <button className="crumb" onClick={onBack}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M14 6l-6 6 6 6"/></svg>
            All billing cycles
          </button>
          <h1>{monthLabel}</h1>
          <p>{rupees(cycle.computed_amount_paise)} commission from {cycle.entry_count} client payment{cycle.entry_count === 1 ? '' : 's'}.</p>
        </div>
        <div className="bar-side">
          <button className="btn-ghost" onClick={onBack}>Back to billing</button>
        </div>
      </header>

      <section className="view">
        <div className="panel steps">
          {[
            { label: 'Cycle closed', sub: monthLabel },
            { label: 'Your invoice', sub: cycle.status === 'open' ? 'Waiting on you' : 'Submitted' },
            { label: 'Admin approval', sub: 'Usually 2–3 days' },
            { label: 'Payment', sub: 'To your bank' },
          ].map((s, i) => {
            const state = stepsFor(cycle.status)[i];
            return (
              <div className={`step ${state}`} key={s.label}>
                <div className="step-dot">{state === 'done' ? '✓' : i + 1}</div>
                <h5>{s.label}</h5><p>{s.sub}</p>
              </div>
            );
          })}
        </div>

        {error && <div className="admin-banner error">{error}</div>}

        {cycle.status === 'open' && (
          <div className="two two-wide">
            <div className="panel form">
              <h3>Upload your {monthLabel} invoice</h3>
              <p>Raise the invoice on GLOS for {rupees(cycle.computed_amount_paise)} excluding tax. The amount has to match exactly, otherwise admin will send it back.</p>
              {windowOpen ? (
                <div className="admin-banner info" style={{ marginTop: 12 }}>
                  Submit by <strong>{fmtDay(cycle.window_closes_on)}</strong>. Invoices are accepted from the 25th to the 5th.
                </div>
              ) : (
                <div className="admin-banner warn" style={{ marginTop: 12 }}>
                  Invoices are accepted from the 25th to the 5th. You can submit this one between <strong>{fmtDay(cycle.window_opens_on)}</strong> and <strong>{fmtDay(cycle.window_closes_on)}</strong>.
                </div>
              )}
              {messages.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Messages from admin</h3>
                  <MessageThread messages={messages} />
                </div>
              )}

              <div className="field">
                <label>Invoice file <em>*</em></label>
                <div
                  className={`drop${dragOver ? ' drag-over' : ''}`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; if (!dragOver) setDragOver(true); }}
                  onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
                  onDrop={handleDrop}
                >
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M12 16V4M7.5 8.5L12 4l4.5 4.5"/><path d="M4 16v2.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V16"/></svg>
                  <b>{file ? file.name : 'Drop your invoice here, or browse'}</b>
                  <small>PDF, Word, Excel or image · up to 50 MB</small>
                  {file && (
                    <div>
                      <button type="button" className="btn-ghost" onClick={clearFile} disabled={busy} style={{ marginTop: 10 }}>
                        Remove file
                      </button>
                    </div>
                  )}
                </div>
                <input type="file" ref={fileInputRef} hidden accept={INVOICE_ACCEPT} onChange={handleFileChange} />
                {fileError && <p style={{ color: 'var(--red)', fontSize: 12.5, marginTop: 6 }}>{fileError}</p>}
                {file && !uploaded && (
                  <button className="btn-ghost" onClick={handleUpload} disabled={busy || suspended || !windowOpen} style={{ marginTop: 8 }}>
                    {busy ? 'Uploading…' : 'Upload file'}
                  </button>
                )}
                {uploaded && <p style={{ color: '#0D8E6E', fontSize: 13, marginTop: 8 }}>Uploaded ✓</p>}
              </div>

              <div className="row2">
                <div className="field">
                  <label>Invoice number</label>
                  <input className="inp" placeholder="e.g. GLOS/2026/08-14" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} maxLength={100} />
                </div>
                <div className="field">
                  <label>Invoice amount, excluding tax <em>*</em></label>
                  <input className="inp num" placeholder="0.00" value={reportedAmount} onChange={(e) => setReportedAmount(e.target.value)} type="number" step="0.01" min="0" max="10000000" />
                  <div className={`match${reportedPaise == null ? '' : amountMismatch ? ' bad' : ' ok'}`}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9"/><path d="M12 16v-5M12 8h.01"/></svg>
                    {reportedPaise == null
                      ? `Must be exactly ${rupees(cycle.computed_amount_paise)}`
                      : amountMismatch
                        ? `Off by ${rupees(Math.abs(cycle.computed_amount_paise - reportedPaise))} — change it to match`
                        : 'Matches the GLOS amount'}
                  </div>
                </div>
              </div>

              <div className="target">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#5A667A" strokeWidth="1.9"><circle cx="12" cy="12" r="8.5"/><circle cx="12" cy="12" r="4"/><circle cx="12" cy="12" r="1"/></svg>
                <small>GLOS computed commission, excluding tax</small><b className="num">{rupees(cycle.computed_amount_paise)}</b>
              </div>

              <div className="form-foot">
                <button
                  className="btn"
                  onClick={handleConfirm}
                  disabled={busy || suspended || !windowOpen || (cycle.computed_amount_paise > 0 && (!uploaded || !reportedAmount || amountMismatch))}
                >
                  Submit for approval
                </button>
                <p>Admin reviews within 2–3 working days</p>
              </div>

              <h3 style={{ marginTop: 26 }}>Or Raise a Question</h3>
              <label>Your concern <em>*</em></label>
              <textarea className="inp" rows={2} maxLength={2000} value={disputeBody} onChange={(e) => setDisputeBody(e.target.value)} placeholder="Describe your concern…" style={{ marginTop: 8 }} />
              <button className="btn-ghost" onClick={handleDispute} disabled={busy || suspended || !disputeBody.trim()} style={{ marginTop: 10 }}>
                Raise a question instead
              </button>
            </div>

            <LedgerPanel ledger={ledger} monthLabel={monthLabel} />
          </div>
        )}

        {cycle.status !== 'open' && (
          <div className="two two-wide">
            <div className="panel">
              {cycle.partner_reported_amount_paise != null && (
                <div style={{ padding: '20px 24px 0' }}>
                  <span className="muted" style={{ fontSize: 13 }}>Your reported amount (excluding tax): </span>
                  <span className="money">{rupees(cycle.partner_reported_amount_paise)}</span>
                </div>
              )}
              {cycle.invoice_file_url && (
                <div style={{ padding: '10px 24px 0' }}>
                  <span className="muted" style={{ fontSize: 13 }}>Uploaded invoice: </span>
                  <a href={cycle.invoice_file_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--blue)', fontSize: 13, fontWeight: 600, overflowWrap: 'anywhere' }}>
                    {cycle.invoice_file_name || 'Open file'}
                  </a>
                  {cycle.invoice_number && <span className="muted" style={{ fontSize: 12.5, marginLeft: 8 }}>Invoice #{cycle.invoice_number}</span>}
                </div>
              )}

              {(cycle.status === 'disputed' || messages.length > 0) && (
                <div style={{ padding: '20px 24px 24px' }}>
                  <h3 style={{ fontSize: 15.5, fontWeight: 600, marginBottom: 12 }}>Discussion</h3>
                  <MessageThread messages={messages} />
                  {cycle.status === 'disputed' && (
                    <>
                      <textarea className="inp" rows={2} maxLength={2000} value={messageBody} onChange={(e) => setMessageBody(e.target.value)} placeholder="Reply to admin…" />
                      <button className="btn" onClick={handleMessage} disabled={busy || !messageBody.trim()} style={{ marginTop: 10 }}>Send</button>
                    </>
                  )}
                </div>
              )}

              {!['disputed'].includes(cycle.status) && (
                <div style={{ padding: '20px 24px 24px' }}>
                  <p className="muted" style={{ fontSize: 13.5 }}>
                    {cycle.status === 'confirmed' && 'Your invoice has been submitted and is waiting on admin approval.'}
                    {cycle.status === 'pending_payment' && 'Approved — payment is scheduled to your bank.'}
                    {cycle.status === 'paid' && `Paid${cycle.settlement_reference ? ` · Ref: ${cycle.settlement_reference}` : ''}.`}
                    {cycle.status === 'failed' && 'The payment attempt failed. Admin has been notified to retry it.'}
                  </p>
                </div>
              )}
            </div>

            <LedgerPanel ledger={ledger} monthLabel={monthLabel} />
          </div>
        )}
      </section>
    </>
  );
}

function LedgerPanel({ ledger, monthLabel }) {
  const total = ledger.reduce((s, l) => s + l.commission_paise, 0);
  return (
    <div className="panel">
      <div className="pane-h"><div><h3>What this covers</h3><p>{ledger.length} client payment{ledger.length === 1 ? '' : 's'} in {monthLabel}</p></div></div>
      <div className="payments">
        {ledger.map((l) => (
          <div className="pay-row" key={l.id}>
            <span className="av" style={{ background: '#8F9BAD', width: 30, height: 30, flex: '0 0 30px', borderRadius: 9, fontSize: 11.5 }}>
              {(l.client_name || l.client_email || '?').slice(0, 2).toUpperCase()}
            </span>
            <div>
              <b style={{ fontWeight: 600, fontSize: 13.5 }}>{l.client_name || l.client_email}</b>
              <div className="sub">{new Date(l.earned_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} · {rupees(l.base_amount_paise)} base · {l.commission_percentage}%</div>
            </div>
            <span className="money">{rupees(l.commission_paise)}</span>
          </div>
        ))}
      </div>
      <div style={{ padding: '14px 24px 22px', borderTop: '1px solid var(--line-soft)', display: 'flex', gap: 10, alignItems: 'center' }}>
        <span style={{ fontSize: 13.5, color: 'var(--text-2)' }}>Total commission</span>
        <span className="money" style={{ marginLeft: 'auto', fontSize: 17 }}>{rupees(total)}</span>
      </div>
    </div>
  );
}
